import { createContext, useContext, useCallback, type ReactNode } from 'react';
import { useNetworkStatus, type NetworkStatus } from '../hooks/useNetworkStatus';

const NetworkContext = createContext<NetworkStatus | null>(null);

interface NetworkProviderProps {
  children: ReactNode;
}

/**
 * Network status provider that tracks connection state globally.
 * Registers a global callback to receive request timing reports from the API module.
 */
export function NetworkProvider({ children }: NetworkProviderProps) {
  // Callback to register the timing handler on the window object
  const registerTimingCallback = useCallback((callback: (durationMs: number) => void) => {
    if (typeof window !== 'undefined') {
      window.__reportRequestTiming = callback;
    }
  }, []);

  const networkStatus = useNetworkStatus(registerTimingCallback);

  return (
    <NetworkContext.Provider value={networkStatus}>
      {children}
    </NetworkContext.Provider>
  );
}

/**
 * Hook to access network status from any component.
 * Must be used within a NetworkProvider.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isOffline, isSlow } = useNetwork();
 *   return <div>{isOffline ? 'Offline' : isSlow ? 'Slow' : 'Online'}</div>;
 * }
 * ```
 */
export function useNetwork(): NetworkStatus {
  const context = useContext(NetworkContext);
  if (!context) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
}
